import { useState } from "react";
import { ChevronRight, FileText, X } from "lucide-react";
import ModalScrim from "./ModalScrim";

export const INTAKE_TABS = ["Manual", "CSV / Excel", "JSON", "Document"];
const FACT_STATES = ["VERIFIED", "KNOWN", "UNKNOWN", "CONFLICTING", "STALE", "INFERRED", "PENDING_CONFIRMATION", "NOT_APPLICABLE"];

export const SAMPLE_CSV = `type,value,source,state,observed
Runtime,Java 17,CMDB re-scan,VERIFIED,23 Aug 2026
Database,Oracle 12c,DBA survey,VERIFIED,23 Aug 2026
Availability,98.4%,Observability export,STALE,01 Jan 2024
KPI owner,,Not captured,UNKNOWN,
`;

export const SAMPLE_JSON = `[
  {"type":"Hosting","value":"Azure AKS","source":"Cloud Discovery","state":"VERIFIED","observed":"23 Aug 2026"},
  {"type":"Open CVEs","value":"1 critical remaining","source":"Security scan","state":"KNOWN","observed":"23 Aug 2026"}
]`;

export const SAMPLE_DOC = `Modernization driver: Cost and peak-season resilience
Runtime: Java 8u202
Database: Oracle 11g
Hosting: On-premise · 3 data centres
Availability: 99.1%
`;

export default function EvidenceIntake({ appId, onClose, onIngest, onManual, preset = null }) {
  const [tab, setTab] = useState(preset?.tab || "Manual");
  const [form, setForm] = useState({
    type: "",
    value: "",
    source: preset?.source || "",
    state: "KNOWN",
    observed: "",
  });
  const [content, setContent] = useState(preset?.content || "");
  const [filename, setFilename] = useState(preset?.filename || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadSample = () => {
    if (tab === "CSV / Excel") setContent(SAMPLE_CSV);
    else if (tab === "JSON") setContent(SAMPLE_JSON);
    else setContent(SAMPLE_DOC);
    setFilename(tab === "JSON" ? "sample.json" : tab === "Document" ? "sample.txt" : "sample.csv");
  };

  const onFile = async (file) => {
    if (!file) return;
    setFilename(file.name);
    setContent(await file.text());
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (tab === "Manual") {
        await onManual({ ...form, source: form.source || "Manual entry" });
      } else {
        const channel = tab === "CSV / Excel" ? "csv" : tab === "JSON" ? "json" : "document";
        await onIngest({
          channel,
          content,
          source: preset?.source || filename || `${channel} ingest`,
          filename,
        });
      }
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Ingest failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalScrim testId="evidence-intake-modal">
      <form className="modal wide" onSubmit={submit} data-testid="evidence-intake-form">
        <button data-testid="close-evidence-modal" type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">DYNAMIC EVIDENCE INGEST · {appId}</p>
        <h2>Add evidence to the foundation</h2>
        <p className="muted">Manual, CSV, JSON and documents become canonical facts with source, observed date and state. UNKNOWN is never converted to yes or no. A conflicting value is recorded as CONFLICTING.</p>
        <div className="tab-row">
          {INTAKE_TABS.map(t => (
            <button key={t} type="button" data-testid={`intake-tab-${t.toLowerCase().replaceAll(" / ", "-").replaceAll(" ", "-")}`} className={tab === t ? "tab active" : "tab"} onClick={() => { setTab(t); setErr(""); }}>{t}</button>
          ))}
        </div>
        {tab === "Manual" && (
          <>
            <label>Fact type
              <input data-testid="evidence-type-input" required value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} placeholder="e.g. Runtime, Database, Availability" />
            </label>
            <label>Value
              <input data-testid="evidence-value-input" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="Leave empty to record UNKNOWN" />
            </label>
            <label>Source
              <input data-testid="evidence-source-input" required value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="e.g. CMDB export · row 42" />
            </label>
            <label>Observed
              <input data-testid="evidence-observed-input" value={form.observed} onChange={e => setForm({ ...form, observed: e.target.value })} placeholder="e.g. 23 Aug 2026" />
            </label>
            <label>Verification state
              <select data-testid="evidence-state-select" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}>
                {FACT_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </>
        )}
        {tab !== "Manual" && (
          <div className="intake-illustration" data-testid="intake-drop-zone">
            <FileText size={30} />
            <strong>{tab} ingest</strong>
            <p className="muted">Upload or paste. Rows become foundation facts. Same key + different value = CONFLICTING. Empty value = UNKNOWN. Dates in 2024 or older become STALE.</p>
            <input type="file" data-testid="evidence-file-input" accept={tab === "JSON" ? ".json,application/json" : tab === "CSV / Excel" ? ".csv,.txt" : ".txt,.md,.doc,.docx"} onChange={e => onFile(e.target.files?.[0])} />
            <button type="button" className="text-button" data-testid="evidence-load-sample" onClick={loadSample}>Load sample {tab}</button>
            <textarea data-testid="evidence-content-input" rows={8} value={content} onChange={e => setContent(e.target.value)} placeholder={tab === "JSON" ? '[{"type":"Runtime","value":"Java 8","source":"CMDB"}]' : tab === "Document" ? "Runtime: Java 8u202" : "type,value,source,state"} required />
          </div>
        )}
        {err && <div className="error-message" data-testid="ingest-error">{typeof err === "string" ? err : JSON.stringify(err)}</div>}
        <button data-testid="submit-evidence-button" className="primary-button full" type="submit" disabled={busy}>{busy ? "Ingesting…" : "Ingest into foundation"} <ChevronRight size={16} /></button>
      </form>
    </ModalScrim>
  );
}
